import type { FieldType, IQuoteStateTransition } from '@/interfaces'

import { addToJSON } from './helper'

export class QuoteStateTransition implements IQuoteStateTransition {
	[key: string]: FieldType

	fromState: number
	toState: number
	label: string
	description: string
	requiresElevatedPermission: boolean
	allowedEstimateMethods: string[] | null
	requiredRole: string

	// Legacy fields for backward compatibility (deprecated)
	requiresComment?: boolean
	action?: string

	// Required for IRecord
	id: number | string

	constructor({
		fromState,
		toState,
		label,
		description,
		requiresElevatedPermission,
		allowedEstimateMethods,
		requiredRole,
		// Legacy fields
		requiresComment,
		action
	}: Partial<IQuoteStateTransition> = {}) {
		this.fromState = Number(fromState || 0)
		this.toState = Number(toState || 0)
		this.label = String(label || '')
		this.description = String(description || '')
		this.requiresElevatedPermission = Boolean(requiresElevatedPermission || false)
		this.allowedEstimateMethods = allowedEstimateMethods || null
		this.requiredRole = String(requiredRole || 'expanded')

		// Legacy fields for backward compatibility
		this.requiresComment = requiresComment
		this.action = action

		// Create unique ID from fromState, toState, and elevated permission to handle duplicates
		this.id = `${this.fromState}-${this.toState}-${this.requiresElevatedPermission}`

		addToJSON(QuoteStateTransition)
	}

	public canUserExecute(userHasElevatedPermission: boolean): boolean {
		// If elevated permission is required, check if user has it
		if (this.requiresElevatedPermission) {
			return userHasElevatedPermission
		}

		// No elevated permission required, anyone can execute
		return true
	}

	public isValidForEstimateMethod(estimateMethod: string): boolean {
		// If no specific methods allowed, it's valid for all
		if (!this.allowedEstimateMethods || this.allowedEstimateMethods.length === 0) {
			return true
		}

		// Check if the estimate method is in the allowed list
		return this.allowedEstimateMethods.includes(estimateMethod)
	}

	public needsComment(): boolean {
		return this.requiresComment || false
	}

	public getDisplayLabel(): string {
		return this.label
	}

	public getDisplayDescription(): string {
		return this.description
	}

	public getAction(): string {
		return this.action || ''
	}

	public isElevatedPermissionAction(): boolean {
		return this.requiresElevatedPermission
	}

	public getAllowedEstimateMethods(): string[] | null {
		return this.allowedEstimateMethods
	}

	public hasRequiredRole(userAccessLevel: number): boolean {
		// Convert string role to access level number for comparison
		// "expanded" = AL.Expanded (2), "full" = AL.Full (3)
		const requiredAccessLevel = this.requiredRole === 'full' ? 3 : 2
		return userAccessLevel >= requiredAccessLevel
	}

	public getRequiredRole(): string {
		return this.requiredRole
	}
}
